// Datum (en tijd) kiezen zonder de standaard browserkiezer: segmenten typen of een eigen kalender in een popover.
// Waarde als tekst in hetzelfde formaat als een native veld ("2026-10-16" of "2026-10-16T14:30"), zodat het een
// directe vervanging is voor <input type="date"> en type="datetime-local".
import { CalendarBlankIcon, CaretLeftIcon, CaretRightIcon, XIcon } from "@phosphor-icons/react";
import { getLocalTimeZone, parseDate, parseDateTime, today, type CalendarDate, type CalendarDateTime } from "@internationalized/date";
import {
  Button as AriaButton,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
} from "react-aria-components";
import { cx } from "../cx.ts";
import { Feedback, FieldLabel, type FieldBase } from "./fields.tsx";

export interface DateFieldProps extends FieldBase {
  value: string;
  onChange: (value: string) => void;
  /** Ook uren en minuten (zoals datetime-local). */
  withTime?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  /** Snelkeuze onder de kalender, bv. "Vandaag" uit de app-catalogus. */
  todayLabel?: string;
  /** Toegankelijke naam van de wisknop, uit de app-catalogus. */
  clearLabel?: string;
  name?: string;
}

type Value = CalendarDate | CalendarDateTime;

function toValue(text: string, withTime: boolean): Value | null {
  if (!text) return null;
  try {
    return withTime ? parseDateTime(text.slice(0, 16)) : parseDate(text.slice(0, 10));
  } catch {
    return null;
  }
}

function fromValue(value: Value | null, withTime: boolean): string {
  if (!value) return "";
  return withTime ? value.toString().slice(0, 16) : value.toString().slice(0, 10);
}

export function DateField({ label, description, errorMessage, optionalText, className, value, onChange, withTime = false, todayLabel, clearLabel, ...rest }: DateFieldProps) {
  const current = toValue(value, withTime);
  const pickToday = () => {
    const day = today(getLocalTimeZone());
    if (!withTime) return onChange(day.toString());
    // Met tijd: de gekozen tijd houden, anders 09:00.
    const time = current && "hour" in current ? current : null;
    onChange(`${day.toString()}T${String(time?.hour ?? 9).padStart(2, "0")}:${String(time?.minute ?? 0).padStart(2, "0")}`);
  };
  return (
    <DatePicker<Value>
      {...rest}
      className={cx("ui-field ui-date", className)}
      value={current}
      onChange={(next) => onChange(fromValue(next, withTime))}
      granularity={withTime ? "minute" : "day"}
      hourCycle={24}
      shouldForceLeadingZeros
    >
      <FieldLabel label={label} optionalText={optionalText} />
      <Group className="ui-input-group ui-date__group">
        <DateInput className="ui-date__input">{(segment) => <DateSegment segment={segment} className="ui-date__segment" />}</DateInput>
        {current && !rest.isRequired && !rest.isDisabled ? (
          <AriaButton className="ui-date__clear" aria-label={clearLabel} onPress={() => onChange("")} slot={null}>
            <XIcon aria-hidden />
          </AriaButton>
        ) : null}
        <AriaButton className="ui-date__button">
          <CalendarBlankIcon aria-hidden />
        </AriaButton>
      </Group>
      <Feedback description={description} errorMessage={errorMessage} />
      <Popover className="ui-popover ui-date__popover" placement="bottom end">
        <Dialog className="ui-date__dialog">
          <Calendar className="ui-calendar">
            <header className="ui-calendar__head">
              <AriaButton slot="previous" className="ui-calendar__nav">
                <CaretLeftIcon aria-hidden />
              </AriaButton>
              <Heading className="ui-calendar__title" />
              <AriaButton slot="next" className="ui-calendar__nav">
                <CaretRightIcon aria-hidden />
              </AriaButton>
            </header>
            <CalendarGrid className="ui-calendar__grid" weekdayStyle="short">
              <CalendarGridHeader>{(day) => <CalendarHeaderCell className="ui-calendar__weekday">{day}</CalendarHeaderCell>}</CalendarGridHeader>
              <CalendarGridBody>{(date) => <CalendarCell date={date} className="ui-calendar__cell" />}</CalendarGridBody>
            </CalendarGrid>
          </Calendar>
          {todayLabel ? (
            <div className="ui-calendar__foot">
              <AriaButton className="ui-calendar__today" onPress={pickToday} slot={null}>
                {todayLabel}
              </AriaButton>
            </div>
          ) : null}
        </Dialog>
      </Popover>
    </DatePicker>
  );
}
